import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { HttpClientWrapper } from "./http-client.wrapper";
import { TranslateService } from "@ngx-translate/core";

/**
 * What a filter selection carries: the `key` of the items the filter offers.
 *
 * Deliberately both: an interactive selection carries whatever the API put in
 * `FiltersData.key` (incident ids come back as numbers), while a selection
 * hydrated from the URL is text, because a query string has no other kind of
 * value. Every hop that passes a selection along is typed with this, so neither
 * half has to be cast into the other.
 */
export type FilterKey = FiltersData["key"];

export interface FiltersData {
  key: string | number;
  keyDisplayed: string;
  checked?: boolean;
  /**
   * i18n key the row's label is rendered from, resolved by the `| translate`
   * pipe at render time. Set only by the option lists this service builds from
   * the catalog (radius, devicesTypes, containerCategory); an API-fed row leaves
   * it unset and keeps the display name the server sent in `keyDisplayed`.
   *
   * The label is deliberately NOT pre-resolved here: `TranslateService.get()`
   * only answers synchronously once the catalog is in memory, and these three
   * lists are fetched while the sidenav's expanded panels first render — i.e.
   * while assets/i18n is still in flight — so a label snapshotted at fetch time
   * came out empty and never re-rendered. Keeping the key means the pipe
   * resolves it whenever the catalog lands, and again on a language switch.
   */
  labelKey?: string;
  /** Interpolation params for `labelKey` ({{start}}/{{end}}, {{maxRadius}}, …). */
  labelParams?: Record<string, string | number>;
}

export interface FilterInputData {
  img: string;
  label: string;
}

export interface FiltersOptions {
  limit: number;
  skip: number;
  search?: string;
}

/**
 * Filters controller service call api rest for the front filters data - return FiltersData
 */

@Injectable()
export class FiltersControllerService {
  constructor(
    private translate: TranslateService,
    public httpClientWrapper: HttpClientWrapper<FiltersData>
  ) {}

  public getFiltersContainers(
    filters: FiltersOptions
  ): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/containerTypes?filter=${JSON.stringify(filters)}`
    );
  }

  public getFiltersDevices(filters: FiltersOptions): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/devices?filter=${JSON.stringify(filters)}`
    );
  }

  public getFiltersSites(filters: FiltersOptions): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/sites?filter=${JSON.stringify(filters)}`
    );
  }

  /**
   * Whether a locally-built row matches the term the user typed. The one place
   * these three lists need their label as TEXT rather than as a key — and the
   * one place it is safe to resolve, because a search term only ever arrives
   * from (debounced) user input, long after the catalog has loaded.
   *
   * Resolved lazily, behind the `!search` guard, so an unsearched fetch — every
   * fetch during bootstrap — never asks for a translation it cannot get yet.
   * Case-insensitive for all three: the radius list used to compare its label
   * case-sensitively, which no caller could reach (it is the only one of the
   * three that is never `searchable`).
   */
  private matchesSearch(row: FiltersData, search?: string): boolean {
    if (!search) {
      return true;
    }

    const term: string = search.toLowerCase();
    const label: string = row.labelKey
      ? this.translate.instant(row.labelKey, row.labelParams)
      : row.keyDisplayed;

    return (
      String(row.key).toLowerCase().includes(term) ||
      String(label).toLowerCase().includes(term)
    );
  }

  public getFilterRadius(filters: FiltersOptions): FiltersData[] {
    const confidenceRadiusRange: string[] = [
      "0-100",
      "101-1000",
      "1001-5000",
      "others"
    ];

    return confidenceRadiusRange
      .map((range) => this.radiusRow(range))
      .filter((row) => this.matchesSearch(row, filters.search));
  }

  /**
   * One confidence-radius bucket: either a `start-end` band or the open-ended
   * `others`. `keyDisplayed` holds the raw bucket, as the fallback the template
   * renders if the label key is ever missing from a catalog.
   */
  private radiusRow(range: string): FiltersData {
    if (!range.includes("-")) {
      return {
        key: range,
        keyDisplayed: range,
        labelKey: `filter.confidenceRadius.${range}`,
        labelParams: { maxRadius: 5000 }
      };
    }

    const [start, end] = range.split("-");

    return {
      key: range,
      keyDisplayed: range,
      labelKey: "filter.confidenceRadius.range",
      labelParams: { start, end }
    };
  }

  public getFilterDevicesTypes(filters: FiltersOptions): FiltersData[] {
    const devicesTypes: string[] = ["SNT", "N5", "SFX"];

    return devicesTypes
      .map((type) => ({
        key: type,
        keyDisplayed: type,
        checked: false,
        labelKey: `filter.devicesTypes.types.${type}`,
        labelParams: { deviceType: type }
      }))
      .filter((row) => this.matchesSearch(row, filters.search));
  }

  public getFilterIncidentsOpened(
    filters: FiltersOptions
  ): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/incidents?filter=${JSON.stringify(filters)}&isOpened=true`
    );
  }

  public getFilterIncidents(
    filters: FiltersOptions
  ): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/incidents?filter=${JSON.stringify(filters)}`
    );
  }

  public getFilterAlertTypes(
    filters: FiltersOptions
  ): Observable<FiltersData[]> {
    return this.httpClientWrapper.get(
      `${environment.apiBaseUrl}filters/alertTypes?filter=${JSON.stringify(filters)}`
    );
  }

  public getFilterContainerCategories(filters: FiltersOptions): FiltersData[] {
    const categories: string[] = ["specific", "polyvalent"];

    return categories
      .map((category) => ({
        key: category,
        keyDisplayed: category,
        checked: false,
        labelKey: `filter.containerCategories.label.${category}`
      }))
      .filter((row) => this.matchesSearch(row, filters.search));
  }
}
